/**
 * Listening and speaking through the app's own server.
 *
 * **Speaking starts with the first sentence.** An answer is split into sentences,
 * all of them are sent for synthesis at once, and each plays the moment the one
 * before it finishes. Somebody hears the first words after one short sentence's
 * worth of synthesis instead of after the whole answer's, and the rest is
 * usually ready before it is needed.
 *
 * **It plays through the one unlocked audio element** (`player.ts`), so a phone
 * does not refuse the sound for arriving too long after the tap.
 */
import type { Lang } from '../../domain/types';
import { logAssistant } from '../log';
import { ASSISTANT_API, type ServerStatus } from '../providers/serverContract';
import { playBlob, stopPlayback } from './player';
import { recordOnce, recordingName, type ListenHandlers, type ListenOptions, type Listening, type SpeechToText, type TextToSpeech } from './types';

/**
 * Break an answer into pieces worth synthesising separately.
 *
 * On sentence ends only — a voice read in fragments sounds broken — and short
 * sentences ride along with the next one, because a clip for "Sure." costs a
 * round trip and buys nothing. No regex lookbehind: older iPhones cannot parse
 * it, and a syntax error there takes the whole app down, not just the voice.
 */
export function splitForSpeech(text: string, min = 40): string[] {
  const flat = text.replace(/\s+/g, ' ').trim();
  if (!flat) return [];
  const sentences: string[] = [];
  let current = '';
  for (let i = 0; i < flat.length; i++) {
    const ch = flat[i];
    current += ch;
    const end = ch === '.' || ch === '!' || ch === '?' || ch === '…';
    if (end && (i + 1 === flat.length || flat[i + 1] === ' ')) {
      sentences.push(current.trim());
      current = '';
    }
  }
  if (current.trim()) sentences.push(current.trim());

  const out: string[] = [];
  for (const s of sentences) {
    if (out.length && out[out.length - 1].length < min) out[out.length - 1] += ` ${s}`;
    else out.push(s);
  }
  // A short last sentence joins the one before rather than making its own trip.
  if (out.length > 1 && out[out.length - 1].length < min) out[out.length - 2] += ` ${out.pop()}`;
  return out;
}

/** `model`: the family's chosen listening model, or empty for the server's default. */
export function createServerStt(model = ''): SpeechToText {
  return {
    id: 'server',
    // Not live: the words arrive when they stop, all at once.
    live: false,
    available: () => typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== 'undefined',
    async start(lang: Lang, handlers: ListenHandlers, opts: ListenOptions = {}): Promise<Listening> {
      return recordOnce(
        async (audio) => {
          const form = new FormData();
          form.append('file', audio, recordingName(audio));
          form.append('lang', lang);
          if (model.trim()) form.append('model', model.trim());
          try {
            const res = await fetch(`${ASSISTANT_API}/transcribe`, { method: 'POST', body: form });
            if (!res.ok) {
              logAssistant('ear', 'fail', `the server could not transcribe (HTTP ${res.status})`, await res.text().catch(() => ''));
              handlers.onError('assistant.voice.failed');
              return;
            }
            const data = (await res.json()) as { text?: string };
            handlers.onFinal((data.text ?? '').trim());
          } catch (e) {
            logAssistant('ear', 'fail', 'the server could not be reached to transcribe', e);
            handlers.onError('assistant.voice.failed');
          }
        },
        handlers.onError,
        handlers.onSound,
        opts.maxMs,
      );
    },
  };
}

/** `model`: the family's chosen voice model, or empty for the server's default. */
export function createServerTts(status: ServerStatus, fallback: TextToSpeech, model = ''): TextToSpeech {
  const chosenModel = model.trim() || undefined;
  /** Bumped by every stop and every new answer; a clip from an older one never plays. */
  let generation = 0;
  let inflight: AbortController | null = null;
  /** Languages the server said no to in this session, so they skip straight to the browser. */
  const refused = new Set<Lang>();

  const stop = () => {
    generation++;
    inflight?.abort();
    inflight = null;
    stopPlayback();
  };

  /**
   * One sentence's audio — with one quick second try.
   *
   * A phone on Wi-Fi drops a request now and then, and the voice service has
   * its busy moments. Without a retry, one such blip on the first sentence
   * meant the whole answer was read in the browser's robot voice and the next
   * answer in the natural one. A refusal that will not change (a 4xx, other
   * than too-many-requests) is not retried.
   */
  const clip = async (text: string, lang: Lang, voice: string | undefined, signal: AbortSignal): Promise<Blob> => {
    let failure: Error | null = null;
    for (let attempt = 0; attempt < 2; attempt++) {
      if (attempt) await new Promise((r) => setTimeout(r, 250));
      if (signal.aborted) break;
      let res: Response;
      try {
        res = await fetch(`${ASSISTANT_API}/speak`, {
          method: 'POST',
          signal,
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ text, lang, voice, model: chosenModel }),
        });
      } catch (e) {
        if (signal.aborted) throw e;
        failure = e instanceof Error ? e : new Error(String(e));
        continue;
      }
      if (res.ok) return res.blob();
      if (res.status === 501) refused.add(lang);
      const detail = (await res.text().catch(() => '')).slice(0, 300);
      failure = new Error(`the server's voice refused (HTTP ${res.status}) ${detail}`);
      if (res.status < 500 && res.status !== 429) break;
    }
    throw failure ?? new Error('the voice request was cancelled');
  };

  return {
    id: 'server',
    available: () => typeof fetch !== 'undefined' && typeof Audio !== 'undefined',

    async voices() {
      try {
        const res = await fetch(`${ASSISTANT_API}/voices${chosenModel ? `?model=${encodeURIComponent(chosenModel)}` : ''}`);
        if (!res.ok) {
          logAssistant('voice', 'fail', `the server refused the voice list (HTTP ${res.status})`, await res.text().catch(() => ''));
          return [];
        }
        const data = (await res.json()) as { voices?: Array<{ id: string; name: string }> };
        return data.voices ?? [];
      } catch (e) {
        logAssistant('voice', 'fail', 'the server could not be reached for the voice list', e);
        return [];
      }
    },

    async speak(text: string, lang: Lang, voiceName?: string) {
      if (!text.trim()) return;
      stop();
      // Thrown, so the layer above speaks it in the browser's voice straight away
      // rather than after a round trip that was always going to fail.
      if (refused.has(lang) || !status.speak?.langs.includes(lang)) throw new Error(`the server has no ${lang === 'he' ? 'Hebrew' : 'English'} voice`);

      const run = ++generation;
      const controller = new AbortController();
      inflight = controller;
      const parts = splitForSpeech(text);
      const pending = parts.map((part) => clip(part, lang, voiceName, controller.signal));
      // The ones never reached (stopped part-way) must not surface as unhandled.
      for (const p of pending) p.catch(() => {});

      for (let i = 0; i < parts.length; i++) {
        try {
          const audio = await pending[i];
          if (run !== generation) return;
          await playBlob(audio);
          if (run !== generation) return;
        } catch (e) {
          if (run !== generation) return;
          // Nothing has been said yet: let the layer above say all of it.
          if (i === 0) throw e;
          // Half an answer said in one voice, the rest in another, beats half an answer.
          logAssistant('voice', 'fail', `the server's voice stopped after ${i} of ${parts.length} sentences — the browser says the rest`, e);
          await fallback.speak(parts.slice(i).join(' '), lang, undefined);
          return;
        }
      }
      if (inflight === controller) inflight = null;
    },

    stop,
  };
}
